import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import ChatContext from '../../services/ChatContext';
import MessageBubbleV2 from './MessageBubbleV2';
import ComposerV2 from './ComposerV2';
import MediaViewerV2 from './MediaViewerV2';
import {
  ThreadCol,
  ThreadHeader,
  Avatar,
  PresenceDot,
  HeaderName,
  HeaderSub,
  HeaderActions,
  IconButton,
  Canvas,
  DayDivider,
  HintBar,
  Empty,
  MsgSkeletonWrap,
  MsgSkeletonRow,
  MsgSkeletonBubble,
  UploadingRow,
  UploadingImage,
  UploadingFile,
  Spinner,
  SpinnerDark,
} from './styles';
import { initialsOf, dayLabel, lastSeenLabel, avatarColor } from './helpers';
import { BackIcon, InfoIcon, ChatIcon } from './icons';

// Skeleton placeholders shown while a conversation's messages load.
const ThreadSkeleton = () => {
  const rows = [
    { owned: false, w: '24rem', h: '3.4rem' },
    { owned: false, w: '16rem', h: '3rem' },
    { owned: true, w: '20rem', h: '3.4rem' },
    { owned: true, w: '28rem', h: '5rem' },
    { owned: false, w: '30rem', h: '4.4rem' },
    { owned: true, w: '14rem', h: '3rem' },
  ];
  return (
    <MsgSkeletonWrap>
      {rows.map((r, i) => (
        <MsgSkeletonRow key={i} owned={r.owned}>
          <MsgSkeletonBubble w={r.w} h={r.h} />
        </MsgSkeletonRow>
      ))}
    </MsgSkeletonWrap>
  );
};

const ThreadV2 = ({ conversation, onBack, onToggleDetail, detailOpen }) => {
  const {
    currentMessages,
    loadingMessages,
    fetchMoreMessages,
    hasMoreMessages,
    fetchingMoreMessages,
    sendingMessage,
    uploadingMedia,
  } = useContext(ChatContext);

  const [lightbox, setLightbox] = useState(null);
  const bottomRef = useRef();
  const canvasRef = useRef();
  const [pinnedBottom, setPinnedBottom] = useState(true);

  const online = conversation?.otherMemberPresence === 'online';
  const conversationId = conversation?.groupId;

  const scrollToBottom = useCallback((behavior = 'auto') => {
    // Wait for layout/paint so the newest content exists before scrolling.
    requestAnimationFrame(() => {
      if (bottomRef.current) bottomRef.current.scrollIntoView({ block: 'end', behavior });
    });
  }, []);

  // Auto-scroll to newest when near the bottom, on send, or when an upload starts.
  useEffect(() => {
    if (pinnedBottom && bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: 'end' });
    }
  }, [currentMessages, sendingMessage, uploadingMedia, pinnedBottom]);

  // Jump to bottom when switching conversations.
  useEffect(() => {
    setPinnedBottom(true);
    scrollToBottom();
  }, [conversationId, scrollToBottom]);

  // Jump to bottom once the INITIAL history finishes loading (not a fragile
  // timeout that races the async fetch). Fires when loadingMessages -> false with
  // messages present, so the thread lands on the newest message on open.
  useEffect(() => {
    if (!loadingMessages && currentMessages && currentMessages.length) {
      setPinnedBottom(true);
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMessages, conversationId]);

  const onScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      setPinnedBottom(scrollHeight - scrollTop - clientHeight < 80);
      if (scrollTop === 0 && hasMoreMessages && !fetchingMoreMessages) {
        fetchMoreMessages();
      }
    },
    [hasMoreMessages, fetchingMoreMessages, fetchMoreMessages],
  );

  // Build render list with day-dividers + grouping flags.
  const rows = useMemo(() => {
    const out = [];
    let prev = null;
    currentMessages.forEach((m) => {
      const prevDay = prev && moment(prev.sentAt * 1000).isSame(moment(m.sentAt * 1000), 'day');
      if (!prevDay) out.push({ kind: 'day', id: `day-${m.id}`, sentAt: m.sentAt });
      // A join/left notice must not start a grouping run. It carries the same
      // user id as the message that follows it, so the first thing anyone says
      // after joining was being treated as a continuation and lost its name and
      // avatar — which is every opening message in a group.
      const isSystem = m.type === 'system';
      const grouped =
        !isSystem &&
        prev &&
        prev.user?.id === m.user?.id &&
        m.sentAt - prev.sentAt < 300 &&
        prevDay;
      const showAvatar = !grouped;
      out.push({ kind: 'msg', id: m.id, message: m, grouped, showAvatar });
      prev = isSystem ? null : m;
    });
    return out;
  }, [currentMessages]);

  const handleCustomAction = (message) => {
    const action = message.customData && message.customData.action;
    if (action && action.kind === 'link' && action.url) {
      window.open(action.url, '_blank', 'noopener');
    }
  };

  return (
    <ThreadCol>
      <ThreadHeader>
        <IconButton
          type="button"
          onClick={onBack}
          style={{ display: 'none' }}
          className="back-btn"
        >
          <BackIcon size={22} />
        </IconButton>
        <Avatar
          src={conversation.user?.avatar}
          bg={avatarColor(conversation.user?.id || conversation.user?.name).bg}
          fg={avatarColor(conversation.user?.id || conversation.user?.name).fg}
          size="4.2rem"
        >
          {!conversation.user?.avatar && initialsOf(conversation.user?.name)}
          {!conversation.isGroup && <PresenceDot online={online} />}
        </Avatar>
        <div>
          <HeaderName>{conversation.user?.name}</HeaderName>
          <HeaderSub online={online}>
            {conversation.isGroup
              ? 'Group'
              : online
              ? 'Online'
              : lastSeenLabel(conversation.otherMemberLastSeenAt)}
          </HeaderSub>
        </div>
        {onToggleDetail && (
          <HeaderActions>
            <IconButton type="button" active={detailOpen} onClick={onToggleDetail} title="Details">
              <InfoIcon size={20} />
            </IconButton>
          </HeaderActions>
        )}
      </ThreadHeader>

      <Canvas ref={canvasRef} onScroll={onScroll}>
        {fetchingMoreMessages && <HintBar>Loading earlier messages…</HintBar>}
        {loadingMessages ? (
          <ThreadSkeleton />
        ) : !currentMessages.length ? (
          <Empty>
            <ChatIcon size={56} style={{ opacity: 0.3 }} />
            <div className="title">Say hello</div>
            <div className="sub">No messages yet — start the conversation below.</div>
          </Empty>
        ) : (
          rows.map((r) =>
            r.kind === 'day' ? (
              <DayDivider key={r.id}>{dayLabel(r.sentAt)}</DayDivider>
            ) : (
              <MessageBubbleV2
                key={r.id}
                message={r.message}
                grouped={r.grouped}
                showAvatar={r.showAvatar}
                isGroup={conversation.isGroup}
                onOpenImage={setLightbox}
                onCustomAction={handleCustomAction}
              />
            ),
          )
        )}

        {uploadingMedia && (
          <UploadingRow>
            {uploadingMedia.isImage && uploadingMedia.url ? (
              <UploadingImage>
                <img src={uploadingMedia.url} alt="" />
                <Spinner centered />
              </UploadingImage>
            ) : (
              <UploadingFile>
                <SpinnerDark size="1.8rem" />
                <span>Uploading {uploadingMedia.name || 'file'}…</span>
              </UploadingFile>
            )}
          </UploadingRow>
        )}

        <div ref={bottomRef} />
      </Canvas>

      <ComposerV2 />
      <MediaViewerV2 src={lightbox} onClose={() => setLightbox(null)} />
    </ThreadCol>
  );
};

export default ThreadV2;
